import { useState } from "react";
import { Box, Column, EmptyState, TemplatesIcon } from "@hightouch/htui";

import {
  ASSET_CARD_ASPECT_RATIO,
  AssetCardFooter,
  AssetCardMedia,
  AssetGrid,
  AssetPickerFooter,
  AssetPickerModal,
  AssetSearchPane,
  type AssetPaneData,
  type AssetSource,
  SelectableAssetCard,
} from "src/components/asset-picker";
import { AdStudioTemplatePreview } from "src/pages/ad-studio/components/ad-studio-template-preview";
import {
  type AdStudioTemplateCard,
  filterAdStudioTemplates,
} from "src/pages/ad-studio/templates/types";

const TEMPLATES_SOURCE_ID = "templates";

type AdStudioTemplatePickerModalProps = {
  templates: AdStudioTemplateCard[];
  initialSelectedIds: string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
};

export function AdStudioTemplatePickerModal({
  templates,
  initialSelectedIds,
  onClose,
  onConfirm,
}: AdStudioTemplatePickerModalProps) {
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds);

  const selectedTemplates = selectedIds.flatMap((id) => {
    const template = templates.find((candidate) => candidate.id === id);
    return template ? [template] : [];
  });

  const toggleTemplate = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  const renderCard = (template: AdStudioTemplateCard) => (
    <TemplateAssetCard
      key={template.id}
      template={template}
      selected={selectedIds.includes(template.id)}
      onToggle={() => toggleTemplate(template.id)}
    />
  );

  const templatesSource: AssetSource = {
    id: TEMPLATES_SOURCE_ID,
    label: "Templates",
    icon: TemplatesIcon,
    count: templates.length,
    pane: (
      <AssetSearchPane
        label="template"
        pluralLabel="templates"
        searchPlaceholder="Search templates"
        useData={(search) => toTemplatePaneData(templates, search)}
        emptyState={(hasSearch) => ({
          title: hasSearch ? "No templates found" : "No templates",
          message: hasSearch
            ? "Try a different search term."
            : "There are no active templates for this brand.",
        })}
        results={{ layout: "grid", renderItem: renderCard }}
      />
    ),
  };

  return (
    <AssetPickerModal
      isOpen
      title="Select templates"
      onClose={onClose}
      sources={[templatesSource]}
      selectedCount={selectedIds.length}
      selectedPane={
        selectedTemplates.length === 0 ? (
          <EmptyState
            title="Nothing selected"
            message="Pick templates from the library. Everything you select shows up here."
          />
        ) : (
          <Column height="100%" minHeight={0} overflowY="auto">
            <AssetGrid>{selectedTemplates.map(renderCard)}</AssetGrid>
          </Column>
        )
      }
      footer={
        <AssetPickerFooter
          onCancel={onClose}
          onConfirm={() => onConfirm(selectedIds)}
          confirmLabel={
            selectedIds.length > 0
              ? `Select ${selectedIds.length} ${selectedIds.length === 1 ? "template" : "templates"}`
              : "Done"
          }
          isConfirmDisabled={false}
        />
      }
    />
  );
}

// Templates are already loaded in full by the caller, so the pane filters them
// client-side and never has more to load.
function toTemplatePaneData(
  templates: AdStudioTemplateCard[],
  search: string,
): AssetPaneData<AdStudioTemplateCard> {
  const items = filterAdStudioTemplates(templates, search);
  return {
    items,
    totalCount: items.length,
    isLoading: false,
    isRefreshing: false,
    isLoadingMore: false,
    hasMore: false,
    loadMore: () => {},
  };
}

type TemplateAssetCardProps = {
  template: AdStudioTemplateCard;
  selected: boolean;
  onToggle: () => void;
};

function TemplateAssetCard({
  template,
  selected,
  onToggle,
}: TemplateAssetCardProps) {
  const { preview } = template.content;

  return (
    <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxLabel={template.name}
        onToggleSelect={onToggle}
        onClick={onToggle}
        footer={
          <AssetCardFooter
            title={template.name}
            subtitle={`${preview.width} × ${preview.height}`}
          />
        }
      >
        <AssetCardMedia>
          <AdStudioTemplatePreview template={template} />
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );
}
